// src/components/laboratory/tabs/PromotionsTab.jsx
import React, { useState } from 'react';
import {
  Plus,
  Tag,
  Search,
  Trash2,
  Calendar,
  CheckCircle,
  XCircle,
  Send,
  Info,
  DollarSign,
  Percent,
  Package,
  QrCode,
  AlertCircle,
  FileDown,
  BarChart3,
  Copy,
  Download,
  Eye,
  CheckSquare,
  ExternalLink,
  XSquare,
  Archive,
  Clock,
  TrendingUp,
  Users,
  X,
  Save,
  MapPin
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_PROMOTION, GENERATE_QR_CODES, GET_PROMOTION_LABELS, GET_LABELS_STATISTICS, DELETE_PROMOTION } from '../../../api/laboratory-api';
import { BROADCAST_NEW_PROMOTION } from '../../../api/notification-api';
import { toast } from 'react-toastify';
import ModalComponent from '../../common/ModalComponent';
import ProductDetailModal from '../modals/ProductDetailModal';
import { getProductImageUrl } from '../../../utils/imageUrl';

const PromotionsTab = ({
  promotions,
  loading,
  laboratory,
  onAddPromotion,
  onEditPromotion,
  refetchPromotions // Refetch function
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState(null);
  const [isQRCodeGenerating, setIsQRCodeGenerating] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState('general');
  const [newQrQty, setNewQrQty] = useState(500);
  const [newEndDate, setNewEndDate] = useState('');
  const [isSavingEndDate, setIsSavingEndDate] = useState(false);
  
  // QR codes tab state - moved to main component level
  const [qrSearchTerm, setQrSearchTerm] = useState('');
  const [qrStatusFilter, setQrStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedProductForDetail, setSelectedProductForDetail] = useState(null);
  const [isProductDetailModalOpen, setIsProductDetailModalOpen] = useState(false);
  const [broadcastNewPromotion, { loading: broadcastLoading }] = useMutation(BROADCAST_NEW_PROMOTION, {
    onCompleted: (data) => {
      if (data.broadcastNewPromotion) toast.success('Notification envoyée à tous les utilisateurs');
      else toast.error('Erreur lors de l\'envoi de la notification');
    },
    onError: (error) => toast.error(`Erreur: ${error.message}`),
  });

  const [deletePromotion, { loading: deleteLoading }] = useMutation(DELETE_PROMOTION, {
    onCompleted: () => {
      toast.success('Promotion supprimée avec succès');
      setIsConfirmDeleteOpen(false);
      setPromotionToDelete(null);
      refetchPromotions(); // Refresh the promotions list
    },
    onError: (error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
      console.error('Error deleting promotion:', error);
    }
  });

  // Query for promotion labels
  const { 
    data: labelsData, 
    loading: labelsLoading, 
    refetch: refetchLabels 
  } = useQuery(GET_PROMOTION_LABELS, {
    variables: { promotionId: selectedPromotion?.id || '' },
    skip: !selectedPromotion?.id,
    fetchPolicy: 'network-only'
  });

  // Query for labels statistics
  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats
  } = useQuery(GET_LABELS_STATISTICS, {
    variables: { promotionId: selectedPromotion?.id || '' },
    skip: !selectedPromotion?.id,
    fetchPolicy: 'network-only'
  });

  // Mutation for updating promotion status
  const [updatePromotion, { loading: updateLoading }] = useMutation(UPDATE_PROMOTION, {
    onCompleted: (data) => {
      if (data.updatePromotion) {
        toast.success('Statut de la promotion mis à jour avec succès');
        refetchPromotions();
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour du statut: ${error.message}`);
    }
  });
  
  const [generateQRCodes] = useMutation(GENERATE_QR_CODES, {
    onCompleted: (data) => {
      if (data.generateQRCodes?.success) {
        toast.success(data.generateQRCodes.message);

        if (data.generateQRCodes.pdfBase64 && data.generateQRCodes.filename) {
          downloadPDFFromBase64(data.generateQRCodes.pdfBase64, data.generateQRCodes.filename);
        } else {
          toast.warning('Codes QR générés mais pas de PDF disponible');
        }

        if (data.generateQRCodes.excelBase64 && data.generateQRCodes.excelFilename) {
          downloadFileFromBase64(data.generateQRCodes.excelBase64, data.generateQRCodes.excelFilename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        }

        setIsQRCodeGenerating(false);
        refetchPromotions();
        // Refetch labels and stats from server to get accurate, persisted data
        refetchLabels();
        refetchStats();
      } else {
        toast.error(data.generateQRCodes?.message || 'Erreur lors de la génération des codes QR');
        setIsQRCodeGenerating(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la génération des codes QR: ${error.message}`);
      setIsQRCodeGenerating(false);
    }
  });

const downloadFileFromBase64 = (base64Data, filename, mimeType) => {
  try {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    toast.error(`Erreur lors du téléchargement: ${error.message}`);
  }
};

// Updated downloadPDFFromBase64 function for PromotionsTab.jsx
const downloadPDFFromBase64 = (base64Data, filename) => {
    try {
      console.log('Starting PDF download...', { filename, base64Length: base64Data?.length });
      
      if (!base64Data) {
        throw new Error('No PDF data provided');
      }
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      console.log('PDF blob created:', { size: blob.size, type: blob.type });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `qr_codes_${Date.now()}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`PDF downloaded successfully: ${link.download}`);
      toast.success(`PDF téléchargé: ${link.download}`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Erreur lors du téléchargement du PDF: ${error.message}`);
    }
  };

  // Days remaining helper
  const getDaysLeft = (endDate) => Math.ceil((new Date(endDate) - new Date()) / 86400000);

  // Filter promotions — expired ones are "archived" and hidden by default
  const filteredPromotions = promotions.filter(promotion => {
    const matchesSearch = promotion.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || promotion.status === statusFilter;
    const isExpired = getDaysLeft(promotion.endDate) < 0;
    const matchesArchive = showArchived ? isExpired : !isExpired;
    return matchesSearch && matchesStatus && matchesArchive;
  });

  const archivedCount = promotions.filter(p => getDaysLeft(p.endDate) < 0).length;

  // Get status display values
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'IN_PROGRESS':
        return {
          text: 'En cours',
          className: 'bg-green-100 text-green-800'
        };
      case 'DRAFT':
        return {
          text: 'Brouillon',
          className: 'bg-yellow-100 text-yellow-800'
        };
      case 'CLOSED':
        return {
          text: 'Terminée',
          className: 'bg-red-100 text-red-800'
        };
      default:
        return {
          text: status,
          className: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Get label status display values
  const getLabelStatusDisplay = (status) => {
    switch(status) {
      case 'SCANNED':
        return {
          text: 'Scanné',
          className: 'bg-green-100 text-green-800',
          icon: CheckSquare
        };
      case 'NOT_SCANNED':
        return {
          text: 'Non scanné',
          className: 'bg-yellow-100 text-yellow-800',
          icon: Eye
        };
      case 'EDITED':
        return {
          text: 'Brouillon',
          className: 'bg-orange-100 text-orange-800',
          icon: Clock
        };
      default:
        return {
          text: status,
          className: 'bg-gray-100 text-gray-800',
          icon: XSquare
        };
    }
  };

  // Check if a promotion is active (current date is between start and end)
  const isPromotionActive = (promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);
    return now >= startDate && now <= endDate && promotion.status === 'IN_PROGRESS';
  };

  // Handle status change
  const handleStatusChange = (promotionId, newStatus) => {
    if (newStatus === 'CLOSED') {
      if (!window.confirm('Êtes-vous sûr de vouloir clôturer cette promotion ? Cette action est irréversible.')) return;
    }
    updatePromotion({
      variables: {
        updatePromotionInput: {
          id: promotionId,
          status: newStatus
        }
      }
    });
  };

  // Handle QR code generation with automatic PDF download
  const handleGenerateQRCodes = (promotion) => {
    console.log('handleGenerateQRCodes called with:', promotion);
    
    if (!promotion.quantity || promotion.quantity <= 0) {
      console.warn('Invalid quantity:', promotion.quantity);
      toast.warning('Veuillez définir une quantité valide pour générer des codes QR');
      return;
    }
    
    console.log(`Starting QR code generation for promotion ${promotion.id} with quantity ${promotion.quantity}`);
    setIsQRCodeGenerating(true);
    
    generateQRCodes({
      variables: {
        promotionId: promotion.id,
        quantity: promotion.quantity
      }
    }).then((result) => {
      console.log('QR Code generation completed:', result);
    }).catch((error) => {
      console.error('QR Code generation failed:', error);
      setIsQRCodeGenerating(false);
    });
  };

  // Handle end date extension
  const handleExtendEndDate = async () => {
    if (!selectedPromotion || !newEndDate) return;
    const current = new Date(selectedPromotion.endDate);
    const chosen = new Date(newEndDate);
    if (chosen <= current) {
      toast.warning('La nouvelle date doit être postérieure à la date de fin actuelle');
      return;
    }
    setIsSavingEndDate(true);
    try {
      await updatePromotion({
        variables: { updatePromotionInput: { id: selectedPromotion.id, endDate: newEndDate } }
      });
      setSelectedPromotion(prev => ({ ...prev, endDate: newEndDate }));
      setNewEndDate('');
      toast.success('Date de fin mise à jour');
    } catch {
      // error handled by mutation onError
    } finally {
      setIsSavingEndDate(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = (promotion) => {
    setPromotionToDelete(promotion);
    setIsConfirmDeleteOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (promotionToDelete) {
      try {
        await deletePromotion({
          variables: { id: promotionToDelete.id }
        });
      } catch (error) {
        console.error('Delete promotion error:', error);
      }
    }
  };

  // Handle send notification
  const handleSendNotification = (promotion) => {
    if (!window.confirm(`Envoyer une notification à tous les pharmaciens et vendeurs pour la promotion "${promotion.name}" ?`)) return;
    broadcastNewPromotion({ variables: { promotionId: promotion.id, promotionName: promotion.name } });
  };

  // Handle promotion click (view details)
  const handlePromotionClick = (promotion) => {
    setSelectedPromotion(promotion);
    setModalActiveTab('general');
    setIsViewModalOpen(true);
  };

  // Handle copy QR code
  const handleCopyQRCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code QR copié dans le presse-papiers');
  };

  // Handle download QR code
  const handleDownloadQRCode = (label) => {
    const link = document.createElement('a');
    link.href = label.image;
    link.download = `qr-code-${label.code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Code QR téléchargé');
  };

// Render QR Codes tab content
const renderQRCodesTab = () => {
  const labels = labelsData?.labelsByPromotion || [];
  const totalGenerated = statsData?.labelsStatistics?.totalGenerated ?? labels.length;
  const scannedCount   = labels.filter(l => l.status === 'SCANNED').length;
  const activeCount    = labels.filter(l => l.status === 'NOT_SCANNED').length;
  const draftCount     = labels.filter(l => l.status === 'EDITED').length;

  const filteredLabels = labels.filter(label => {
    const matchesSearch = label.code.toLowerCase().includes(qrSearchTerm.toLowerCase()) ||
                         (label.user && `${label.user.firstName} ${label.user.lastName}`.toLowerCase().includes(qrSearchTerm.toLowerCase()));
    const matchesStatus = qrStatusFilter === 'all' || 
                         (qrStatusFilter === 'used' && label.isUsed) ||
                         (qrStatusFilter === 'unused' && !label.isUsed) ||
                         (qrStatusFilter === label.status);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Stats summary — 4 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => setQrStatusFilter('all')}
        >
          <QrCode className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-blue-600 leading-tight">Total</p>
            <p className="text-xl font-bold text-blue-800">{totalGenerated.toLocaleString()}</p>
          </div>
        </div>
        <div
          className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 cursor-pointer hover:bg-green-100 transition-colors"
          onClick={() => setQrStatusFilter('NOT_SCANNED')}
        >
          <Eye className="h-6 w-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-green-600 leading-tight">Actifs</p>
            <p className="text-xl font-bold text-green-800">{activeCount.toLocaleString()}</p>
          </div>
        </div>
        <div
          className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2 cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setQrStatusFilter('EDITED')}
        >
          <Clock className="h-6 w-6 text-orange-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-orange-600 leading-tight">Brouillon</p>
            <p className="text-xl font-bold text-orange-700">{draftCount.toLocaleString()}</p>
          </div>
        </div>
        <div
          className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2 cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => setQrStatusFilter('SCANNED')}
        >
          <CheckSquare className="h-6 w-6 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-purple-600 leading-tight">Scannés</p>
            <p className="text-xl font-bold text-purple-800">{scannedCount.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Rechercher un code QR ou utilisateur..."
            value={qrSearchTerm}
            onChange={(e) => setQrSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative w-full sm:w-48">
          <select
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={qrStatusFilter}
            onChange={(e) => setQrStatusFilter(e.target.value)}
          >
            <option value="all">Tous les codes</option>
            <option value="used">Utilisés</option>
            <option value="unused">Non utilisés</option>
            <option value="SCANNED">Scannés</option>
            <option value="NOT_SCANNED">Non scannés</option>
            <option value="EDITED">Brouillon</option>
          </select>
        </div>
      </div>

      {/* Draft warning banner */}
      {draftCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2 text-sm text-orange-800">
          <Clock size={15} className="flex-shrink-0 text-orange-500" />
          <span><span className="font-semibold">{draftCount}</span> code{draftCount > 1 ? 's' : ''} en statut <span className="font-semibold">Brouillon</span> — en attente de confirmation de collage par le laboratoire.</span>
        </div>
      )}

      {/* Loading State */}
      {labelsLoading && (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des codes QR...</p>
        </div>
      )}

      {/* QR Codes List */}
      {!labelsLoading && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {filteredLabels.length === 0 ? (
            <div className="text-center py-8">
              <QrCode size={40} className="mx-auto text-gray-400 mb-2" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun code QR</h3>
              <p className="mt-1 text-sm text-gray-500">
                {qrSearchTerm || qrStatusFilter !== 'all' ? 
                  "Aucun code QR ne correspond à vos critères." : 
                  "Aucun code QR généré pour cette promotion."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code QR
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Région
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ville
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date d'utilisation
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLabels.map((label) => {
                    const statusDisplay = getLabelStatusDisplay(label.status);
                    const StatusIcon = statusDisplay.icon;
                    const isDraft = label.status === 'EDITED';

                    return (
                      <tr key={label.id} className={isDraft ? 'bg-orange-50/60 hover:bg-orange-50' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap ${isDraft ? 'border-l-2 border-orange-400' : ''}`}>
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 h-10 w-10 rounded-md flex items-center justify-center mr-3 ${isDraft ? 'bg-orange-100' : 'bg-gray-100'}`}>
                              <QrCode className={`h-5 w-5 ${isDraft ? 'text-orange-500' : 'text-gray-600'}`} />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 font-mono">
                                {label.code}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {label.id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <StatusIcon className="h-4 w-4 mr-2 text-gray-400" />
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusDisplay.className}`}>
                              {statusDisplay.text}
                            </span>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          {label.user ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {label.user.firstName} {label.user.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {label.user.email}
                              </div>
                              {label.user.pharmacy && (
                                <div className="text-xs text-blue-600">
                                  {label.user.pharmacy.namePharmacy}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Non utilisé</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {label.pharmacy?.region ? (
                            <span>{label.pharmacy.region.nomRegion}</span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {label.pharmacy?.ville ? (
                            <span>{label.pharmacy.ville.nomVilleVillage}</span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {label.date ? (
                            <div>
                              <div>{format(new Date(label.date), 'dd/MM/yyyy', { locale: fr })}</div>
                              <div className="text-xs">{format(new Date(label.date), 'HH:mm', { locale: fr })}</div>
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>
                            <div>{format(new Date(label.createdAt), 'dd/MM/yyyy', { locale: fr })}</div>
                            <div className="text-xs">{format(new Date(label.createdAt), 'HH:mm', { locale: fr })}</div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleCopyQRCode(label.code)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Copier le code"
                            >
                              <Copy size={16} />
                            </button>
                            
                            <button
                              onClick={() => handleDownloadQRCode(label)}
                              className="text-green-600 hover:text-green-900"
                              title="Télécharger le QR code"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

  // Render Stats tab content
  const renderStatsTab = () => {
    if (statsLoading) {
      return (
        <div className="text-center py-10">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
          <p className="mt-2 text-gray-500">Chargement des statistiques…</p>
        </div>
      );
    }

    const stats = statsData?.labelsStatistics;
    if (!stats) {
      return (
        <div className="text-center py-12 text-gray-500">
          <BarChart3 size={48} className="mx-auto text-gray-300 mb-3" />
          <p>Aucune donnée disponible pour cette promotion.</p>
        </div>
      );
    }

    const dailyScans = stats.dailyScans || [];

    // Filter daily scans to the promotion's start→end date range
    const promoStart = selectedPromotion?.startDate
      ? new Date(selectedPromotion.startDate).toISOString().slice(0, 10)
      : null;
    const promoEnd = selectedPromotion?.endDate
      ? new Date(selectedPromotion.endDate).toISOString().slice(0, 10)
      : null;
    const rangeScans = dailyScans.filter(d => {
      if (promoStart && d.date < promoStart) return false;
      if (promoEnd && d.date > promoEnd) return false;
      return true;
    });
    const maxDaily = rangeScans.reduce((m, d) => Math.max(m, d.count), 0) || 1;

    return (
      <div className="space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
            <QrCode className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-600">QR générés</p>
              <p className="text-2xl font-bold text-blue-800">{stats.totalGenerated}</p>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-green-600">Utilisés</p>
              <p className="text-2xl font-bold text-green-800">{stats.totalUsed}</p>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3">
            <XCircle className="h-8 w-8 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-yellow-600">Non utilisés</p>
              <p className="text-2xl font-bold text-yellow-800">{stats.totalUnused}</p>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center space-x-3">
            <BarChart3 className="h-8 w-8 text-purple-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-purple-600">Taux d'usage</p>
              <p className="text-2xl font-bold text-purple-800">{(stats.usageRate ?? 0).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        {/* Time-based stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Aujourd'hui",   value: stats.scannedToday || 0 },
            { label: 'Cette semaine', value: stats.scannedThisWeek || 0 },
            { label: 'Ce mois',       value: stats.scannedThisMonth || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-800">{value}</p>
              <p className="text-xs text-gray-400">scans</p>
            </div>
          ))}
        </div>

        {/* Scan evolution chart — filtered to promotion date range */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-500" />
            Évolution des scans
          </h4>
          {selectedPromotion && (
            <p className="text-xs text-gray-400 mb-3">
              {format(new Date(selectedPromotion.startDate), 'dd/MM/yyyy', { locale: fr })}
              {' → '}
              {format(new Date(selectedPromotion.endDate), 'dd/MM/yyyy', { locale: fr })}
            </p>
          )}
          {rangeScans.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
              Aucune donnée de scan sur cette période
            </div>
          ) : (
            <>
              <div className="flex items-end gap-0.5 h-32">
                {rangeScans.map((d, i) => {
                  const pct = Math.round((d.count / maxDaily) * 100);
                  return (
                    <div
                      key={i}
                      className="flex flex-col items-center flex-1 min-w-[4px]"
                      title={`${d.date}: ${d.count} scan${d.count !== 1 ? 's' : ''}`}
                    >
                      <div
                        className="w-full bg-blue-500 hover:bg-blue-600 rounded-t transition-colors"
                        style={{ height: `${pct}%`, minHeight: d.count > 0 ? 3 : 0 }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{rangeScans[0]?.date || ''}</span>
                <span>{rangeScans[rangeScans.length - 1]?.date || ''}</span>
              </div>
            </>
          )}
        </div>

        {/* Top vendeurs (pharmacies with most scans) */}
        {stats.topPharmacies?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Users size={16} className="text-green-500" />
              Top vendeurs
            </h4>
            <div className="space-y-2">
              {stats.topPharmacies.map((item, i) => {
                const pct = Math.round((item.count / (stats.topPharmacies[0]?.count || 1)) * 100);
                return (
                  <div key={item.pharmacy.id} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-4 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{item.pharmacy.namePharmacy}</p>
                    </div>
                    <div className="w-28 bg-gray-100 rounded-full h-2 flex-shrink-0">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-14 text-right flex-shrink-0">
                      {item.count} scan{item.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          {showArchived ? `Archives (${archivedCount})` : `Promotions actives (${promotions.length - archivedCount})`}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived(v => !v)}
            className={`inline-flex items-center px-3 py-1.5 text-sm leading-4 font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              showArchived
                ? 'bg-gray-700 text-white border-gray-700 focus:ring-gray-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-gray-400'
            }`}
          >
            <Archive className="-ml-0.5 mr-1 h-4 w-4" />
            Archives{archivedCount > 0 && ` (${archivedCount})`}
          </button>
        </div>
      </div>

      {/* Search and filter bar */}
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Rechercher une promotion..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative w-full sm:w-40">
          <select
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="IN_PROGRESS">En cours</option>
            <option value="DRAFT">Brouillon</option>
            <option value="CLOSED">Terminée</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des promotions...</p>
        </div>
      ) : filteredPromotions.length === 0 ? (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
          <Tag size={40} className="mx-auto text-gray-400 mb-2" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune promotion</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' ? 
              "Aucune promotion ne correspond à vos critères." : 
              "Ce laboratoire n'a pas encore de promotions."}
          </p>
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="mt-3 text-sm text-green-600 hover:text-green-500"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promotion
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jours restants
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commissions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPromotions.map((promotion) => {
                const statusDisplay = getStatusDisplay(promotion.status);
                const now = new Date();
                const startDate = new Date(promotion.startDate);
                const endDate = new Date(promotion.endDate);
                const isActive = now >= startDate && now <= endDate;
                
                return (
                  <tr 
                    key={promotion.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handlePromotionClick(promotion)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-indigo-600">{promotion.name}</div>
                      <div className="text-xs text-gray-500">
                        {promotion.quantity ? `${promotion.quantity} codes QR` : 'Quantité non définie'}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        <div>
                          <div>Début: {format(startDate, 'dd/MM/yyyy', { locale: fr })}</div>
                          <div>Fin: {format(endDate, 'dd/MM/yyyy', { locale: fr })}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const days = getDaysLeft(promotion.endDate);
                        if (days < 0) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Expiré</span>;
                        if (days === 0) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"><Clock size={12} className="mr-1"/>Dernier jour</span>;
                        if (days <= 3) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"><Clock size={12} className="mr-1"/>{days}j</span>;
                        if (days <= 7) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700"><Clock size={12} className="mr-1"/>{days}j</span>;
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"><Clock size={12} className="mr-1"/>{days}j</span>;
                      })()}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col text-sm gap-1">
                        <div className="flex items-center gap-1">
                          <DollarSign size={13} className="text-green-500 flex-shrink-0" />
                          <span className="font-medium text-green-700">Vendeur:</span>
                          <span className="font-semibold">{promotion.commissionAmount} DH</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Percent size={13} className="text-blue-500 flex-shrink-0" />
                          <span className="font-medium text-blue-700">Mazid:</span>
                          {promotion.commissionType === 'FIXED_AMOUNT' ? (
                            <span className="font-semibold">{promotion.commissionPercentage} DH</span>
                          ) : (
                            <span className="font-semibold">{promotion.commissionPercentage}%</span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {promotion.products && promotion.products.length > 0 ? (
                        <div className="flex flex-col space-y-1">
                          {promotion.products.slice(0, 2).map(product => (
                            <button
                              key={product.id}
                              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 text-left"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductForDetail(product);
                                setIsProductDetailModalOpen(true);
                              }}
                            >
                              <Package size={14} className="mr-1 flex-shrink-0" />
                              <span className="truncate max-w-[120px]">{product.name}</span>
                              <Eye size={12} className="ml-1 flex-shrink-0" />
                            </button>
                          ))}
                          {promotion.products.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{promotion.products.length - 2} autres
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Aucun produit</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusDisplay.className}`}>
                          {statusDisplay.text}
                        </span>
                        
                        {!isActive && now > endDate && (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Expirée
                          </span>
                        )}
                        
                        {!isActive && now < startDate && (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            À venir
                          </span>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div 
                        className="flex items-center justify-end space-x-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* QR Code Generation Button - Enhanced */}
                        {promotion.status === 'DRAFT' && promotion.quantity && (
                          <button
                            onClick={() => handleGenerateQRCodes(promotion)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors"
                            title={`Générer ${promotion.quantity} codes QR (PDF automatique)`}
                            disabled={isQRCodeGenerating}
                          >
                            {isQRCodeGenerating ? (
                              <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                            ) : (
                              <QrCode size={18} />
                            )}
                          </button>
                        )}
                        
                        {promotion.status === 'CLOSED' && isActive && (
                          <button
                            onClick={() => handleStatusChange(promotion.id, 'IN_PROGRESS')}
                            className="text-green-600 hover:text-green-900"
                            title="Activer"
                            disabled={updateLoading}
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        
                        {promotion.status === 'IN_PROGRESS' && isActive && (
                          <button
                            onClick={() => handleStatusChange(promotion.id, 'CLOSED')}
                            className="text-red-600 hover:text-red-900"
                            title="Clôturer"
                            disabled={updateLoading}
                          >
                            <XCircle size={18} />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleSendNotification(promotion)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Envoyer une notification"
                        >
                          <Send size={18} />
                        </button>
                        
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Promotion Detail Modal — custom overlay */}
      {isViewModalOpen && selectedPromotion && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsViewModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusDisplay(selectedPromotion.status).className}`}>
                    {getStatusDisplay(selectedPromotion.status).text}
                  </span>
                  {(() => { const d = getDaysLeft(selectedPromotion.endDate); return d < 0 ? <span className="text-xs text-gray-400">Expirée</span> : d <= 7 ? <span className="text-xs text-orange-500 font-medium">{d}j restants</span> : null; })()}
                </div>
                <h2 className="text-lg font-bold text-gray-900 truncate">{selectedPromotion.name}</h2>
              </div>
              <button onClick={() => setIsViewModalOpen(false)} className="ml-4 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex-shrink-0">
                <X size={18} />
              </button>
            </div>

            {/* Tab Bar */}
            <div className="flex border-b border-gray-100 px-6">
              {[
                { key: 'general', label: 'Détails', icon: Info },
                { key: 'qrcodes', label: 'QR Codes', icon: QrCode },
                { key: 'stats', label: 'Statistiques', icon: BarChart3 },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setModalActiveTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    modalActiveTab === key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">

              {/* ── DETAILS TAB ── */}
              {modalActiveTab === 'general' && (
                <div className="space-y-5">
                  {/* Key metrics row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                      <p className="text-xs text-green-600 font-medium mb-1">Vendeur / scan</p>
                      <p className="text-xl font-bold text-green-700">{selectedPromotion.commissionAmount} DH</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                      <p className="text-xs text-blue-600 font-medium mb-1">Mazid</p>
                      <p className="text-xl font-bold text-blue-700">
                        {selectedPromotion.commissionType === 'FIXED_AMOUNT'
                          ? `${selectedPromotion.commissionPercentage} DH`
                          : `${selectedPromotion.commissionPercentage}%`}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 font-medium mb-1">QR codes</p>
                      <p className="text-xl font-bold text-gray-700">{selectedPromotion.quantity ?? '—'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 font-medium mb-1">Type</p>
                      <p className="text-sm font-semibold text-gray-700">{selectedPromotion.commissionType === 'PERCENTAGE' ? 'Pourcentage' : 'Montant fixe'}</p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Période</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Début</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {format(new Date(selectedPromotion.startDate), 'dd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Fin actuelle</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {format(new Date(selectedPromotion.endDate), 'dd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                    </div>

                    {/* Extend end date */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 mb-2">Prolonger la durée</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={newEndDate}
                          min={new Date(new Date(selectedPromotion.endDate).getTime() + 86400000).toISOString().split('T')[0]}
                          onChange={e => setNewEndDate(e.target.value)}
                          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={handleExtendEndDate}
                          disabled={!newEndDate || isSavingEndDate}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Save size={13} />
                          {isSavingEndDate ? 'Sauvegarde...' : 'Enregistrer'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Seule une date postérieure à la date de fin actuelle est acceptée.</p>
                    </div>
                  </div>

                  {/* Products */}
                  {selectedPromotion.products && selectedPromotion.products.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Produits associés</p>
                      <ul className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedPromotion.products.map(product => (
                          <li key={product.id} className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {product.image
                                ? <img src={getProductImageUrl(product.image)} alt={product.name} className="w-9 h-9 object-cover" />
                                : <Package size={14} className="text-gray-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                              {product.price && <p className="text-xs text-gray-500">{product.price.toFixed(2)} DH</p>}
                            </div>
                            <button
                              className="text-blue-500 hover:text-blue-700"
                              onClick={() => { setIsViewModalOpen(false); setSelectedProductForDetail(product); setIsProductDetailModalOpen(true); }}
                            >
                              <ExternalLink size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Regions */}
                  {selectedPromotion.regions && selectedPromotion.regions.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Régions cibles</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedPromotion.regions.map(r => (
                          <span key={r.id} className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full">
                            <MapPin size={9} /> {r.nomRegion}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PDF */}
                  {selectedPromotion.pdfFile && (
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <FileDown size={18} className="text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 flex-1 truncate">{selectedPromotion.pdfFile}</span>
                      <a
                        href={`${process.env.REACT_APP_API_URL?.replace('/graphql', '')}/api/contracts/download/${selectedPromotion.pdfFile}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                      >
                        <Download size={13} /> Télécharger
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* ── QR CODES TAB ── */}
              {modalActiveTab === 'qrcodes' && (
                <div className="space-y-5">
                  {/* Add QR codes panel */}
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-purple-800 mb-1">Ajouter des QR codes</p>
                    <p className="text-xs text-purple-600 mb-3">
                      Les nouveaux codes s'ajouteront aux {(labelsData?.labelsByPromotion?.length ?? 0)} codes existants en statut <span className="font-semibold text-orange-600">Brouillon</span> jusqu'à confirmation par le labo.
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        step="100"
                        value={newQrQty}
                        onChange={e => setNewQrQty(parseInt(e.target.value) || 100)}
                        className="w-32 px-3 py-1.5 text-sm border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="text-sm text-purple-700 font-medium">nouveaux QR codes</span>
                      <button
                        onClick={() => {
                          if (!newQrQty || newQrQty <= 0) { toast.warning('Quantité invalide'); return; }
                          // Send total = existing confirmed + new qty so the backend creates exactly newQrQty new labels
                          const confirmedCount = (labelsData?.labelsByPromotion || []).filter(l => l.status !== 'EDITED').length;
                          setIsQRCodeGenerating(true);
                          generateQRCodes({ variables: { promotionId: selectedPromotion.id, quantity: confirmedCount + newQrQty } })
                            .catch(() => setIsQRCodeGenerating(false));
                        }}
                        disabled={isQRCodeGenerating}
                        className="ml-auto flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isQRCodeGenerating ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Génération...</> : <><QrCode size={13} />Générer</>}
                      </button>
                    </div>
                  </div>

                  {/* Generation history */}
                  {(() => {
                    const allLabels = labelsData?.labelsByPromotion || [];
                    if (allLabels.length === 0) return null;
                    // Group labels into batches: labels created within 5 minutes of each other = same batch
                    const sorted = [...allLabels].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    const batches = [];
                    sorted.forEach(label => {
                      const t = new Date(label.createdAt).getTime();
                      const last = batches[batches.length - 1];
                      if (last && t - last.startTime < 5 * 60 * 1000) {
                        last.count++;
                        last.statuses.add(label.status);
                      } else {
                        batches.push({ startTime: t, date: new Date(label.createdAt), count: 1, statuses: new Set([label.status]) });
                      }
                    });
                    return (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                          <Clock size={12} /> Historique des générations
                        </p>
                        <div className="space-y-1.5">
                          {batches.map((batch, i) => {
                            const isDraft = batch.statuses.has('EDITED') && !batch.statuses.has('NOT_SCANNED') && !batch.statuses.has('SCANNED');
                            const isConfirmed = !batch.statuses.has('EDITED');
                            return (
                              <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-xs">
                                <div className="flex items-center gap-2 text-gray-600">
                                  <QrCode size={11} className="text-gray-400" />
                                  <span className="font-semibold text-gray-800">{batch.count} code{batch.count > 1 ? 's' : ''}</span>
                                  <span className="text-gray-400">·</span>
                                  <span>{format(batch.date, 'dd MMM yyyy à HH:mm', { locale: fr })}</span>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full font-medium ${isDraft ? 'bg-orange-100 text-orange-700' : isConfirmed ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {isDraft ? 'Brouillon' : isConfirmed ? 'Confirmé' : 'Mixte'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {renderQRCodesTab()}
                </div>
              )}

              {/* ── STATS TAB ── */}
              {modalActiveTab === 'stats' && renderStatsTab()}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle size={24} />
            <h3 className="text-lg font-medium">Attention</h3>
          </div>
          
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer la promotion <strong>{promotionToDelete?.name}</strong> ?
          </p>
          
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100">
            <p className="text-sm text-yellow-800">
              Cette action est irréversible. Tous les codes QR associés à cette promotion seront également supprimés.
            </p>
          </div>
          
          {promotionToDelete?.products && promotionToDelete.products.length > 0 && (
            <p className="text-sm text-gray-500">
              Cette promotion est associée à {promotionToDelete.products.length} produit(s).
            </p>
          )}
        </div>
      </ModalComponent>

      {/* Product Detail Modal */}
      {isProductDetailModalOpen && selectedProductForDetail && (
        <ProductDetailModal
          product={selectedProductForDetail}
          isOpen={isProductDetailModalOpen}
          onClose={() => {
            setIsProductDetailModalOpen(false);
            setSelectedProductForDetail(null);
          }}
          laboratory={laboratory}
          promotions={promotions}
          adds={[]}
        />
      )}

      {/* QR Code Loading Modal */}
      <ModalComponent
        isOpen={isQRCodeGenerating}
        onClose={() => {}}
        showFooter={false}
        title="Génération des codes QR en cours"
        size="sm"
      >
        <div className="text-center py-6">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent mb-4"></div>
          <p className="text-gray-700">
            Génération et préparation du fichier PDF...
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Veuillez patienter, cette opération peut prendre quelques instants.
          </p>
        </div>
      </ModalComponent>
    </div>
  );
};

export default PromotionsTab;